/** M9 round two: the pure rules of the sign-in (brief `docs/briefs/m9-your-own-agent.md`, task 2, B2), shared by the API's before-hook and
 *  the web's consent page. The "Your own AI" round (DECISIONS "2026-10-05 — Your own AI for every agent") opened the return addresses to
 *  every agent: one rule, `returnAddressKind`, says where a sign-in code may be sent. */

/** Claude's one return address for the hosted apps (web, desktop, mobile, Cowork) */
export const CLAUDE_CALLBACK = "https://claude.ai/api/mcp/auth_callback";

/** The hosted apps' return addresses we know by name (`docs/research/mcp-clients-2026-10-05.md`, "Allow-list changes to make"): each exact,
 *  Google's relay by its host and the `/r/` prefix (one address per user and connector). Grok is held back until its real request is seen. */
export const KNOWN_RETURN_ADDRESSES = Object.freeze({
  exact: Object.freeze([
    CLAUDE_CALLBACK,
    "https://chatgpt.com/connector_platform_oauth_redirect", // ChatGPT (we answer with iss, RFC 9207, so its stable address applies)
    "https://vscode.dev/redirect", // VS Code for the web and its client document
    "https://www.cursor.com/agents/mcp/oauth/callback", // Cursor's web and cloud agents
    "cursor://anysphere.cursor-mcp/oauth/callback", // Cursor's desktop app, registered beside its loopback and web addresses
    "https://vertexaisearch.cloud.google.com/oauth-redirect", // Gemini Enterprise (useful once we issue pre-registered clients)
  ]),
  prefixes: Object.freeze([Object.freeze({ host: "oauth-redirect.googleusercontent.com", path: "/r/" })]), // the Gemini app's relay
});

/** Where a sign-in code may go: `loopback` (http on exactly localhost, 127.0.0.1 or [::1], any port, any path: every command-line agent),
 *  `known` (a hosted app named above), `unknown-https` (any other https address: accepted, the consent page warns and asks the member to
 *  confirm), `refused` (everything else: plain http elsewhere, look-alike hosts, credentials or a fragment, https on a loopback host, any
 *  other scheme, anything that is not an address). */
export type ReturnAddressKind = "loopback" | "known" | "unknown-https" | "refused";
/** the raw form, so a host the URL parser would rewrite (`0x7f000001`, `127.1`) is never taken for loopback */
const LOOPBACK = /^http:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d{1,5})?([/?].*)?$/;
const LOOPBACK_HOST = /^(localhost|.+\.localhost|127(\.\d{1,3}){3}|\[::1\])$/i;

export function returnAddressKind(uri: string): ReturnAddressKind {
  const url = parsed(uri);
  if (!url || uri.includes("#") || url.username || url.password) return "refused";
  if ((KNOWN_RETURN_ADDRESSES.exact as readonly string[]).includes(uri)) return "known";
  if (url.protocol === "http:") return LOOPBACK.test(uri) ? "loopback" : "refused";
  if (url.protocol !== "https:" || !url.hostname || LOOPBACK_HOST.test(url.hostname)) return "refused";
  return KNOWN_RETURN_ADDRESSES.prefixes.some((p) => uri.startsWith(`https://${p.host}${p.path}`) && uri.length > `https://${p.host}${p.path}`.length && !url.search) ? "known" : "unknown-https";
}
const parsed = (uri: string): URL | null => { try { return new URL(uri); } catch { return null; } };

/** a registration with any loopback address is a native app (the library takes a registration without `application_type` for a web app,
 *  which may not use http://localhost; native accepts https addresses off loopback too) */
export const hasLoopback = (uris: readonly string[]) => uris.some((u) => returnAddressKind(u) === "loopback");

/** The workspace slug of a connector address, when the address is exactly `<origin>/mcp/<slug>` (no trailing slash, no query); else null. */
export function slugOfConnectorAddress(address: string, origin: string): string | null {
  const prefix = `${origin.replace(/\/+$/, "")}/mcp/`;
  if (!address.startsWith(prefix)) return null;
  const slug = address.slice(prefix.length);
  return /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/.test(slug) ? slug : null;
}

/** The host a member is shown for an app: the host of a URL-shaped client id (Claude's document, `claude.ai`), else null (a registration's
 *  random id names no host; the page then shows the return address's host alone). Never the app's self-chosen name. */
export function clientHostOf(clientId: string | null | undefined): string | null {
  if (!clientId || !/^https:\/\//.test(clientId)) return null;
  try { return new URL(clientId).hostname; } catch { return null; }
}
